import { useState, useEffect, useMemo } from "react";
import AdminLayout from "../../../layouts/AdminLayout";
import { getStoredAuthUser, getCurrentRoleKey } from "../../../services/authService";
import { getSocieties } from "../../../services/societyService";
import Skeleton from "../../../components/ui/Skeleton";
import SearchInput from "../../../components/ui/SearchInput";
import EmptyState from "../../../components/ui/FeedbackStates";
import CommunityMessages from "../messages/CommunityMessages";
import styles from "./SocietyChatsWrapper.module.css";
import { Building2, MessageSquare, ArrowRight } from "lucide-react";
import { resolveBackendUrl } from "../../../services/api";

const getTypeBadgeClass = (type) => {
  const t = String(type || "").toUpperCase();
  if (t.includes("COMMERCIAL")) return styles.typeCommercial;
  if (t.includes("INDUSTRIAL")) return styles.typeIndustrial;
  if (t.includes("HYBRID")) return styles.typeHybrid;
  return styles.typeResidential;
};

export default function SocietyChatsWrapper() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [societies, setSocieties] = useState([]);
  const [selectedSocietyId, setSelectedSocietyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const authUser = getStoredAuthUser();
    const currentRole = getCurrentRoleKey();
    setUser(authUser);
    setRole(currentRole);

    if (currentRole === "super-admin") {
      fetchSocieties();
    } else if (authUser?.society_id) {
      setSelectedSocietyId(authUser.society_id);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchSocieties = async () => {
    try {
      const data = await getSocieties();
      if (data && data.societies) {
        setSocieties(data.societies);
      } else if (Array.isArray(data)) {
        setSocieties(data);
      }
    } catch (error) {
      console.error("Error fetching societies:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSocieties = useMemo(() => {
    if (!searchTerm) return societies;
    const lower = searchTerm.toLowerCase();
    return societies.filter((soc) => {
      const name = soc.name || soc.society_name || "";
      return name.toLowerCase().includes(lower);
    });
  }, [societies, searchTerm]);

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ padding: "40px 24px" }}>
          <Skeleton variant="grid" count={6} />
        </div>
      </AdminLayout>
    );
  }

  if (role === "super-admin" && !selectedSocietyId) {
    return (
      <AdminLayout>
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.titleArea}>
              <div className={styles.iconBadge}>
                <MessageSquare size={22} />
              </div>
              <div>
                <h1 className={styles.title}>Society Chats & Channels</h1>
                <p className={styles.subtitle}>
                  Select a property to view, monitor, and participate in its community conversation.
                </p>
              </div>
            </div>

            <div style={{ width: "280px" }}>
              <SearchInput
                placeholder="Search properties..."
                value={searchTerm}
                onChange={(val) => setSearchTerm(typeof val === "string" ? val : (val?.target?.value ?? ""))}
                onClear={() => setSearchTerm("")}
              />
            </div>
          </div>

          {filteredSocieties.length > 0 ? (
            <div className={styles.grid}>
              {filteredSocieties.map((soc) => {
                const id = soc.id || soc.society_id;
                const name = soc.name || soc.society_name || `Property #${id}`;
                const icon = soc.icon_url ? resolveBackendUrl(soc.icon_url) : null;
                const propType = soc.society_type || soc.property_type?.name || "Residential";

                return (
                  <div
                    key={id}
                    className={styles.card}
                    onClick={() => setSelectedSocietyId(Number(id))}
                  >
                    <div className={styles.cardTop}>
                      {icon ? (
                        <img src={icon} alt={name} className={styles.cardIconImg} />
                      ) : (
                        <div className={styles.cardIcon}>
                          <Building2 size={24} />
                        </div>
                      )}
                      <div className={styles.cardInfo}>
                        <h3 className={styles.cardTitle}>{name}</h3>
                        <p className={styles.cardMeta}>Property ID: #{id}</p>
                      </div>
                    </div>

                    <div className={styles.cardBottom}>
                      <span className={`${styles.typeBadge} ${getTypeBadgeClass(propType)}`}>
                        {propType}
                      </span>
                      <span className={styles.openAction}>
                        Open Channel <ArrowRight size={14} />
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: "32px 0" }}>
              <EmptyState
                title="No properties found"
                description={`No properties found matching "${searchTerm}"`}
              />
            </div>
          )}
        </div>
      </AdminLayout>
    );
  }

  if (!selectedSocietyId) {
    return (
      <AdminLayout>
        <div className={styles.container}>
          <div className={styles.emptyState}>
            You do not have a property assigned. Please contact your administrator.
          </div>
        </div>
      </AdminLayout>
    );
  }

  const handleResetSociety = role === "super-admin" ? () => setSelectedSocietyId(null) : null;

  return (
    <CommunityMessages
      societyId={selectedSocietyId}
      currentUser={user}
      onBack={handleResetSociety}
    />
  );
}
